import { useEffect, useMemo, useState } from 'react';
import { apiClient } from '@/lib/apiClient';

/**
 * Ф-Фронт (ADR-0070): батч-запрос короткоживущих signed-URL для ключей фото
 * ручек/крючков (`Handle.photoKeys` / `primaryPhotoKey`) → map `key → url` для
 * рендера `<img>`. Клон `useDecorImageUrls` (тот же frozen-контракт
 * `POST /api/media/signed-url`, ADR-0046, P-011); префикс ключей `handles/`|`hooks/`
 * изолирует от декор-ключей в общем бакете.
 *
 * - Принимает список ключей видимых карточек (`string | null | undefined`).
 *   `null`/`undefined` (нет фото) — отбрасываются; дубликаты схлопываются.
 * - Батчинг: контракт принимает 1..50 ключей за запрос (P-011). Список бьётся на
 *   чанки ≤50 и запрашивается параллельно, результаты мёржатся.
 * - Состояния: `loading` (идёт запрос), `error` (любой чанк упал). Частичный
 *   успех сохраняется: `urls` содержит то, что удалось подписать.
 * - Refetch на истечение TTL (3600с) — ВНЕ объёма (битый `<img>` → onError →
 *   плейсхолдер в карточке).
 *
 * Security: signed-URL держатся только в state (память сессии), не логируются и
 * не персистятся (короткоживущие токены). P-009 — фото пользователя тут ни при чём.
 */

const MAX_BATCH = 50;

export interface UseHandleImageUrlsResult {
  /** key → signed url. Отсутствие ключа = нет подписи (ещё грузится / ошибка / нет фото). */
  urls: Record<string, string>;
  loading: boolean;
  error: boolean;
}

function chunk<T>(arr: readonly T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

export function useHandleImageUrls(
  keys: readonly (string | null | undefined)[],
): UseHandleImageUrlsResult {
  // Уникальные не-null ключи, стабильно отсортированы → стабильный dep.
  const uniqueKeys = useMemo(() => {
    const set = new Set<string>();
    for (const k of keys) if (k) set.add(k);
    return [...set].sort();
  }, [keys]);
  // JSON.stringify — стабильный dep без коллизий (ключи содержат пробелы/спецсимволы).
  const keysKey = JSON.stringify(uniqueKeys);

  const [urls, setUrls] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (uniqueKeys.length === 0) {
      setUrls({});
      setLoading(false);
      setError(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(false);

    const batches = chunk(uniqueKeys, MAX_BATCH);
    Promise.allSettled(batches.map((batch) => apiClient.media.signedUrls({ keys: batch }))).then(
      (results) => {
        if (cancelled) return;
        const merged: Record<string, string> = {};
        let anyError = false;
        for (const r of results) {
          if (r.status === 'fulfilled') {
            for (const [key, entry] of Object.entries(r.value.urls)) merged[key] = entry.url;
          } else {
            anyError = true;
          }
        }
        setUrls(merged);
        setError(anyError);
        setLoading(false);
      },
    );

    return () => {
      cancelled = true;
    };
    // keysKey — стабильная сериализация uniqueKeys (массив меняет identity каждый рендер).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keysKey]);

  return { urls, loading, error };
}
